import { cn } from '@/Domain/utils/cn';

/**
 * Stepper dot row shared by the manual checklist and the automatic
 * post-completion stepper. Pure presentational: each caller passes its own
 * `totalSteps` + `currentIndex`, so progress in the two flows stays
 * independent — advancing dot N in the manual flow has no effect on the
 * automatic flow's dots, and vice versa.
 */
interface OnboardingProgressDotsProps {
  totalSteps: number;
  /** Zero-based index of the active step. Dots at `idx <= currentIndex` render filled. */
  currentIndex: number;
  /** Prefix for dot `data-testid`s — e.g. `gs-onboarding-progress-dot` → `gs-onboarding-progress-dot-1`. */
  testIdPrefix: string;
}

export const OnboardingProgressDots = ({
  totalSteps,
  currentIndex,
  testIdPrefix,
}: OnboardingProgressDotsProps) => (
  <div className="flex items-center justify-center gap-2" data-testid={`${testIdPrefix}s`}>
    {Array.from({ length: totalSteps }).map((_, idx) => (
      <span
        // Index is a stable, meaningful key here — the dots have no identity
        // beyond position — so the lint rule's usual warning doesn't apply.
        // eslint-disable-next-line react/no-array-index-key
        key={idx}
        className={cn(
          'size-2 rounded-full transition-colors duration-200',
          idx <= currentIndex ? 'bg-foreground' : 'bg-foreground/20',
        )}
        data-testid={`${testIdPrefix}-${idx + 1}`}
      />
    ))}
  </div>
);
